import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Link2, RefreshCw } from 'lucide-react';
import StatusBadge from '../../components/whatsapp/StatusBadge';
import PairWhatsAppModal from '../../components/whatsapp/PairWhatsAppModal';
import TestMessageCard from '../../components/whatsapp/TestMessageCard';
import { getSessionStatus, reconnectSession } from '../../api/whatsapp/ownerWhatsAppApi';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '../../components/ui';

const OwnerWhatsAppPage = () => {
  const [status, setStatus] = useState('DISCONNECTED');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await getSessionStatus();
      if (res.success) setStatus(res.status);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Poll status every 10s
    return () => clearInterval(interval);
  }, []);

  const handleReconnect = async () => {
    setLoading(true);
    try {
      await reconnectSession();
      await fetchStatus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header Card */}
      <Card>
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <Link
              to="/notification-apps"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 transition-colors hover:text-primary-700 dark:hover:text-primary-400"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Back to Notification apps</span>
            </Link>
            <h1 className="text-xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
              WhatsApp Engine Settings
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Manage device pairing &amp; socket connection status for automated messaging.
            </p>
          </div>
          <div className="shrink-0">
            <StatusBadge status={status} />
          </div>
        </CardContent>
      </Card>

      {/* Grid Layout for Actions & Test Card */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Actions Card */}
        <Card className="flex flex-col justify-between">
          <CardHeader className="border-b bg-neutral-50/50 py-4">
            <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
              Device Management
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 p-5">
            <p className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">
              Pair your official school phone via pairing code to send transactional receipts, fee reminders, and announcements.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                variant="primary"
                leftIcon={<Link2 className="h-4 w-4" />}
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto"
              >
                Link Device
              </Button>

              <Button
                type="button"
                variant="secondary"
                isLoading={loading}
                leftIcon={<RefreshCw className="h-4 w-4" />}
                onClick={handleReconnect}
                className="w-full sm:w-auto"
              >
                Force Reconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manual Test Dispatch Card */}
        <TestMessageCard />
      </div>

      <PairWhatsAppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchStatus}
      />
    </div>
  );
};

export default OwnerWhatsAppPage;